import type { Cluster, Poll } from '../types/teaser'

export const heroOct = {
  src: 'https://cdn.magicpatterns.com/uploads/rFmfjvckDw5ivzL1PNgF9i/Screenshot_2026-08-13_at_09.59.42.png',
  alt: 'OCT B-scan of a macula with diabetic macular oedema showing large cystoid intraretinal spaces',
}

export const inputsOct = {
  src: 'https://cdn.magicpatterns.com/uploads/tsTw4fzsJkro73oReEGMy2/Screenshot_2026-08-13_at_11.00.09.png',
  alt: 'OCT angiography en-face images with corresponding structural B-scans and flow signal overlays',
  caption: 'Retinal imaging · Munk et al. 2022',
}

export const heroPromise = 'What if OCT could reveal phenotype — not just fluid?'
export const heroCredibility = 'AI-assisted OCT analysis · 2,355 DME eyes · 4 phenotypes'
export const heroAnswer =
  'AI-assisted analysis of 2,355 DME eyes identified four distinct morphological phenotypes from familiar OCT biomarkers.'
export const heroPunchline = ['The pattern was already there.', 'AI helped make it visible.']

export const unmetNeedHeadline = 'Not every patient improves to the same extent.'

export const introPoll: Poll = {
  question:
    'How many DME patients do you think show incomplete functional improvement with anti-VEGF therapy?',
  options: [
    { id: 'a', label: 'Around 10%', share: 16 },
    { id: 'b', label: 'Around 20%', share: 33 },
    { id: 'c', label: 'Up to 40%', share: 51 },
  ],
  resultNote: 'of your peers answered the same',
}

export const introReward =
  'Previous studies reported incomplete functional improvement in 31–46% of patients treated with anti-VEGF.'
export const introBridge = 'So why do some patients respond differently?'

export const peerLabel = 'Your answer · peer responses'

export const whyDiffer = {
  headline: 'DME is not one morphological pattern.',
  image:
    'https://cdn.magicpatterns.com/uploads/uso358xDhUModmpzSk3Cwv/Screenshot_2026-08-13_at_10.32.49.png',
  imageAlt:
    'Two AI-segmented OCT B-scans side by side showing markedly different DME morphology within the same diagnosis',
  caption: 'Midena AI-segmented cluster comparison',
  copy: 'Patients with DME can present with different combinations of fluid distribution, inflammatory features and retinal structural change.',
  then: 'AI-assisted OCT analysis has shown that these familiar features can combine into distinct morphological phenotypes.',
  takeaway: 'Same diagnosis. Different phenotype.',
  cta: 'See how the patterns were identified',
}

export const togetherHeadline = 'What if we could assess these features together?'
export const togetherBridge = '5 biomarkers → 4 phenotypes'

export const octVariables = ['IRF', 'SRF', 'I-HRF', 'ELM', 'EZ']

export const inputs = {
  headline: ['5 OCT variables.', 'One clustering model.'],
  variables: 'IRF · SRF · I-HRF · ELM · EZ',
  support: 'Analysed together across 2,355 DME eyes.',
  transition: 'So what emerged when they were analysed together?',
  cta: 'See the patterns',
}

export const clusters: Cluster[] = [
  {
    id: 'c1',
    name: 'Cluster 1',
    descriptor: 'Early morphological pattern',
    signature: 'Mainly central IRF · no SRF · intact ELM/EZ · lower I-HRF',
    image:
      'https://cdn.magicpatterns.com/uploads/w1fpPhXp3y2fUyuJ1hZDAq/Screenshot_2026-08-13_at_10.00.57.png',
    advanced: false,
  },
  {
    id: 'c2',
    name: 'Cluster 2',
    descriptor: 'Intermediate pattern',
    signature: 'Mainly peripheral IRF · no SRF · intact ELM/EZ · increased I-HRF',
    image:
      'https://cdn.magicpatterns.com/uploads/wA1fzd9agrR64vUqd1aTJs/Screenshot_2026-08-13_at_10.01.03.png',
    advanced: false,
  },
  {
    id: 'c3',
    name: 'Cluster 3',
    descriptor: 'Advanced morphological pattern',
    signature: 'Central + peripheral IRF · SRF · EZ disruption · higher I-HRF',
    image:
      'https://cdn.magicpatterns.com/uploads/5hd1C7Zt7hdQ24QGL1MxpX/Screenshot_2026-08-13_at_10.01.11.png',
    advanced: true,
  },
  {
    id: 'c4',
    name: 'Cluster 4',
    descriptor: 'More advanced morphological pattern',
    signature: 'Central + peripheral IRF · greater SRF · ELM/EZ disruption · higher I-HRF',
    image:
      'https://cdn.magicpatterns.com/uploads/ntXBsF4KgJafCb5QsAnAGa/Screenshot_2026-08-13_at_10.01.19.png',
    advanced: true,
  },
]

export function clusterById(id: string): Cluster {
  return clusters.find((c) => c.id === id) ?? clusters[0]
}

export const phenotypeHeadline = ['One diagnosis.', 'Four phenotypes.']
export const phenotypeSupport =
  'Four distinct morphological DME patterns emerged from the combined OCT biomarker profiles.'
export const phenotypeCue = 'AI-assisted OCT analysis · 2,355 eyes'

export const takeaway = {
  headline: 'The difference may already be visible.',
  lead: 'The question is no longer only:',
  first: '“How much fluid is there?”',
  second: '“What phenotype am I looking at?”',
  support: 'The signal lies in the combination of familiar OCT features.',
}

export const confidencePoll: Poll = {
  question: 'How confident are you today in identifying inflammatory DME phenotypes on OCT?',
  options: [
    { id: 'very', label: 'Very confident', share: 13 },
    { id: 'somewhat', label: 'Somewhat confident', share: 47 },
    { id: 'not', label: 'Not very confident', share: 40 },
  ],
  resultNote: 'of your peers answered the same',
}

export const confidenceReward = 'Precision diagnostics starts with recognising the pattern.'

export const references = {
  intro: 'OCT: Cehofski clinical case.',
  abraham:
    'Previous anti-VEGF studies cited in Abraham et al. reported 31–46% incomplete functional improvement. Abraham JR et al. Am J Ophthalmol. 2021;222:328–339.',
  munk: 'Retinal imaging figure: Munk et al. 2022.',
  midena: 'Midena E, et al. AI-assisted OCT biomarker analysis, 2,355 DME eyes.',
}

export const disclaimer =
  'Non-promotional disease awareness. OCT imagery: Cehofski clinical case, Munk et al. 2022 and Midena AI-segmented cluster examples. The analysis identified morphological phenotype clusters through OCT biomarker patterns — not treatment groups, treatment-response categories or a validated severity staging system.'
